<template>
  <div>
    <div class="heading" :class="{ 'spinner': isLoading }">
      <h3>Scheduled Patients</h3>
    </div>
    <div class="scheduled-patient-div" v-if="!isLoading">
      <DataTable :value="sortedPatientDataWithSerialNumbers" paginator :alwaysShowPaginator=false :rows=5
        tableStyle="min-width: 10rem" tableClass="editable-cells-table">
        <template v-if="!sortedPatientDataWithSerialNumbers?.length && !isLoading" #empty>
          <p class="no-records-class"> No Scheduled Patients</p>
        </template>
        <Column field="serialNumber" header="" sortable>
        </Column>
        <Column field="given" header="PATIENT NAME" sortable>
          <template #body="{ data }">
            {{ data.given }} {{ data.family }}
          </template>
        </Column>
        <Column field="name" header="REQUESTING PHYSICIAN" sortable></Column>
        <Column field="gender" header="AGE/GENDER">
          <template #body="{ data }">
            {{ calculateAge(data.birthdate) }}/{{ data.gender }}
          </template>
        </Column>
        <Column field="mrn" header="MRN"></Column>
        <Column field="diagnosis" header="DIAGNOSIS">
        </Column>
        <Column field="tbName" header="TUMOR BOARD">
        </Column>
        <Column field="notes" header="NOTE/REQUEST">
        </Column>
        <Column field="priority" header="PRIORITY">
          <template #body="{ field, data }">
            <div :class="getPriorityClass(data[field])">
              {{ data[field] }}
            </div>
          </template>
        </Column>
        <Column field="date" header="DATE">
        </Column>
        <Column field="" header="">
          <template #body="slotProps">
            <span @click="openDeleteModal(slotProps.data)" class="material-symbols-outlined icon-button-primary-filled"
              title="Remove patient from schedule">remove</span>
          </template>
        </Column>
      </DataTable>
    </div>
    <Transition name="fade">
      <div class="modal" v-show="showDelete">
        <DeleteModal cardName="Remove from Schedule" :showDelete="true"
          :actionAndEntity="`remove ${itemToDelete.given} ${itemToDelete.family} from the schedule`"
          @close-delete="closeDeleteModal()" @delete="handleDelete">
        </DeleteModal>
      </div>
    </Transition>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { getPatientDetailsByMRN } from '@/ux3/cards/TumorBoardSideBar/getPatientDetailsByMRN'
import { calculateAge, formatDate, mergeByMrn, mergeByPhysicianId, showToast } from '@/utils/utils.js'
import { addToAgenda } from '@/ux3/cards/TumorBoardSessions/addToAgenda'
import { getScheduleAPI } from '@/ux3/cards/TumorBoardSideBar/getScheduleAPI'
import DeleteModal from '@/ux3/components/Common/DeleteModal.vue'

const store = useStore()
const schedulePatientsFromStore = computed(() => store.state.session.schedulePatients)
const scheduledPatients = ref([])
const toBeScheduled = ref([])
const patientDetailsByMRN = ref([])
const requestingPhysician = computed(() => store.state.session.requestingPhysician)
const sortedPatientDataWithSerialNumbers = ref([])
const sortedToBeSchedulePatientDataWithSerialNumbers = ref([])
const isLoading = ref(true)
const showDelete = ref(false)
const itemToDelete = ref({})

const emit = defineEmits(['reset-confirmation'])

const handleMoveToUnscheduled = async (rowData) => {
  showDelete.value = false
  if (!rowData) { return }
  //PATCH with just schedule_id and tb_id for unscheduled
  const rowDataObject = {
    schedule_id: rowData.schedule_id,
    tb_id: rowData.calendar.tb_id,
  }
  const response = await addToAgenda([rowDataObject])

  if (response?.schedule_ids) {
    showToast('success', 'Patient successfully removed from schedule.')
    const schedulePatientFromResponse = await getScheduleAPI()
    store.dispatch('setScheduledAndToBeScheduledPatients', { schedulePatients: schedulePatientFromResponse, publish: true })
    emit('reset-confirmation')
  } else if (response?.status === 409) {
    const result = await response.json()
    showToast('error', result.detail)
  } else {
    showToast('error', 'Failed to remove patient. Please try again.')
  }
}

const closeDeleteModal = () => {
  showDelete.value = false
  itemToDelete.value = {}
}

const openDeleteModal = (data) => {
  showDelete.value = true
  itemToDelete.value = data
}

const handleDelete = async () => {
  await handleMoveToUnscheduled(itemToDelete.value)
}

watch(schedulePatientsFromStore, async (newSchedulePatients) => {
  if (!newSchedulePatients) { return }

  scheduledPatients.value = newSchedulePatients.scheduled
  toBeScheduled.value = newSchedulePatients.not_scheduled
  const mrnListScheduled = newSchedulePatients.scheduled?.map((item) => item.mrn) || []
  const mrnListToBeScheduled = newSchedulePatients.not_scheduled?.map((item) => item.mrn) || []
  const mrnList = [...mrnListScheduled, ...mrnListToBeScheduled]
  if (mrnList.length > 0) {
    const patientDetailsByMRNFromResponse = await getPatientDetailsByMRN(mrnList)
    patientDetailsByMRN.value = patientDetailsByMRNFromResponse
  }
  scheduledPatients.value = tableDataWithMRNPhysicianDetails(scheduledPatients.value)
  toBeScheduled.value = tableDataWithMRNPhysicianDetails(toBeScheduled.value)
  sortedPatientDataWithSerialNumbers.value = scheduledPatients.value?.map((item, index) => {
    return {
      ...item,
      tbName: item.calendar.tumor_board.tb_name,
      date: formatDate(item.calendar.date),
      serialNumber: index + 1,
    } || []
  })
  sortedToBeSchedulePatientDataWithSerialNumbers.value = scheduledPatients.value?.map((item, index) => {
    return { ...item, serialNumber: index + 1 } || []
  })
  isLoading.value = false
}, { immediate: true })

const tableDataWithMRNPhysicianDetails = (array) => {
  let tableData = array
  if (array?.length && patientDetailsByMRN.value?.length) {
    tableData = mergeByMrn(array, patientDetailsByMRN.value)
  }
  if (array?.length && requestingPhysician.value?.length) {
    tableData = mergeByPhysicianId(tableData, requestingPhysician.value)
  }
  return tableData
}

const getPriorityClass = (priority) => priority ? `${priority.toLowerCase()}-priority` : ''

</script>

<style>
.scheduled-patient-div .p-column-header-content {
  font-size: 12px;
  font-weight: 500;
  color: var(--Grey600);
  font-family: 'Roboto';
}

.scheduled-patient-div .p-datatable .p-sortable-column:first-child .p-sortable-column-icon {
  margin-left: 0;
}

.scheduled-patient-div .p-datatable .p-datatable-tbody>tr>td {
  font-size: 14px;
  font-weight: 400;
  color: var(--Black);
  font-family: 'Roboto';
}

.no-records-class {
  color: var(--Black);
  font-size: 1.125rem;
  font-weight: 500;
  text-align: center;
}

.p-datatable {
  border: 1px solid var(--Grey100);
  margin: 0 28px;
}

.heading {
  margin-left: 12px;
  margin-right: 12px;
}

.low-priority,
.standard-priority,
.urgent-priority {
  font-weight: bold;
}

.low-priority {
  color: var(--Grey500);
}

.standard-priority {
  color: var(--WarningYellowDark);
}

.urgent-priority {
  color: var(--ErrorRedDark);
}

.modal {
  position: absolute;
  top: 65px;
  left: calc(50% - 23vw);
}
</style>
